import { cn } from '@/utils'
import React from 'react'
import { FragmentGrid } from './FragmentGrid'
import { Timer } from './Timer'

export interface SquareFragment {
  id: string
  sourceCoords: { x: number; y: number; width: number; height: number }
  isCorrect: boolean
}

export interface SquareTask {
  id: string
  title: string
  description: string
  imageUrl: string
  emptySpotPosition: { top: string; left: string; width: string; height: string }
  emptySpotCoords: { x: number; y: number; width: number; height: number }
  fragments: SquareFragment[]
}

export interface TaskResult {
  taskId: string
  selectedFragmentId: string | null
  correctFragmentId: string
  isCorrect: boolean
  timeSpent: number
}

export type TaskStage = 'intro' | 'task' | 'result'

export interface SquareFragmentTaskProps {
  imageUrl?: string
  tasks?: SquareTask[]
  timeLimit?: number
  onComplete?: (results: TaskResult[]) => void
  onStart?: () => void
  className?: string
  isDraggable?: boolean
}

interface TaskScreenProps {
  currentTask: SquareTask
  currentTaskIndex: number
  totalTasks: number
  timeLimit: number
  remainingTime: number
  isDragging: boolean
  placedFragmentId: string | null
  draggedFragmentId: string | null
  isDraggable: boolean
  onDragStart: (e: React.DragEvent<HTMLDivElement>, fragmentId: string) => void
  onDragEnd: () => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void
  onClick: (fragmentId: string) => void
  formatTime: (seconds: number) => string
  fragmentRefs: React.RefObject<Record<string, HTMLDivElement | null>>
  onStart?: () => void
  onComplete?: () => void
}

export function TaskScreen({
  currentTask,
  currentTaskIndex,
  totalTasks,
  timeLimit,
  remainingTime,
  isDragging,
  placedFragmentId,
  draggedFragmentId,
  isDraggable,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onClick,
  formatTime,
  fragmentRefs,
  onStart,
  onComplete
}: TaskScreenProps) {
  React.useEffect(() => {
    if (onStart) {
      onStart()
    }
  }, [onStart])

  React.useEffect(() => {
    if (onComplete && currentTaskIndex === totalTasks - 1 && placedFragmentId) {
      onComplete()
    }
  }, [onComplete, currentTaskIndex, totalTasks, placedFragmentId])

  return (
    <div className="flex flex-col items-center gap-6">
      <h2 className="text-center text-xl font-bold">
        {currentTask.title} ({currentTaskIndex + 1}/{totalTasks})
      </h2>

      <Timer
        formatTime={formatTime}
        onStart={onStart}
        remainingTime={remainingTime}
        timeLimit={timeLimit}
      />

      <p className="text-center">{currentTask.description}</p>

      <div className="w-full max-w-2xl">
        <div
          className="relative mb-8 overflow-hidden rounded-md border "
          style={{ minHeight: '300px' }}
        >
          <img
            alt="Image background"
            className="h-full w-full object-contain object-center"
            src={currentTask.imageUrl || '/placeholder.svg'}
          />

          <div
            className={cn(
              'absolute transition-all duration-200',
              isDragging
                ? 'border-2 border-dashed border-indigo-500 bg-white'
                : 'border-2 border-dashed border-gray-300 bg-white',
              placedFragmentId ? 'border-solid border-green-500' : ''
            )}
            onDragOver={onDragOver}
            onDrop={onDrop}
            style={{
              top: currentTask.emptySpotPosition.top,
              left: currentTask.emptySpotPosition.left,
              width: currentTask.emptySpotPosition.width,
              height: currentTask.emptySpotPosition.height
            }}
          >
            {placedFragmentId && (
              <div className="h-full w-full">
                {currentTask.fragments.map((fragment) => {
                  if (fragment.id === placedFragmentId) {
                    return (
                      <div
                        className="h-full w-full"
                        key={fragment.id}
                        style={{
                          backgroundImage: `url(${currentTask.imageUrl})`,
                          backgroundSize: `${600}px ${400}px`,
                          backgroundPosition: `-${fragment.sourceCoords.x}px -${fragment.sourceCoords.y}px`
                        }}
                      />
                    )
                  }
                  return null
                })}
              </div>
            )}
          </div>
        </div>

        <FragmentGrid
          currentTask={currentTask}
          draggedFragmentId={draggedFragmentId}
          fragmentRefs={fragmentRefs}
          fragments={currentTask.fragments}
          isDraggable={isDraggable}
          onClick={onClick}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          placedFragmentId={placedFragmentId}
        />

        <div className="flex items-center justify-center gap-2 text-center text-sm text-gray-500">
          <svg
            fill="none"
            height="16"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M14 8a2 2 0 1 0-4 0v4a2 2 0 1 0 4 0z" />
            <path d="M12 2v2" />
            <path d="M12 20v2" />
            <path d="m4.93 4.93 1.41 1.41" />
            <path d="m17.66 17.66 1.41 1.41" />
            <path d="M2 12h2" />
            <path d="M20 12h2" />
            <path d="m6.34 17.66-1.41 1.41" />
            <path d="m19.07 4.93-1.41 1.41" />
          </svg>
          {isDraggable
            ? 'Przeciągnij fragment na puste miejsce w obrazku'
            : 'Kliknij fragment, aby umieścić go w pustym miejscu'}
        </div>
      </div>
    </div>
  )
}
